import { useState, useRef, useCallback, useEffect } from "react";
import { BlockRenderer } from "../BlockRenderer";
import { BlockEditForm } from "./BlockEditForm";
import { BlockTypeSelector } from "./BlockTypeSelector";
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  Settings2,
  X,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface Block {
  id: string;
  block_type: string;
  content: Record<string, any>;
  sort_order: number;
}

const BLOCK_LABELS: Record<string, string> = {
  text_section: "Text",
  image: "Image",
  two_column: "Two Column",
  material_reference: "Materials",
  gallery: "Gallery",
  cta: "CTA",
  image_hotspot: "Hotspot",
  bullet_list: "Bullets",
  material_strategy: "Strategy",
  result: "Result",
  specs: "Specs",
  challenges: "Challenges",
  outcomes: "Outcomes",
  quote: "Quote",
  scenarios: "Scenarios",
};

/* Block types that have a settings drawer for complex properties */
const NEEDS_DRAWER = new Set([
  "image",
  "two_column",
  "gallery",
  "material_reference",
  "image_hotspot",
  "cta",
  "specs",
  "challenges",
  "outcomes",
  "bullet_list",
  "material_strategy",
  "result",
  "quote",
  "scenarios",
]);

/* Block types that support direct inline text editing */
const INLINE_TEXT_BLOCKS = new Set(["text_section", "two_column", "cta"]);

interface InlineEditorProps {
  blocks: Block[];
  onChange: (blocks: Block[]) => void;
  article: {
    project_name: string;
    system_name?: string | null;
    hero_image_url?: string | null;
  };
}

export function InlineEditor({ blocks, onChange, article }: InlineEditorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const drawerBlock = blocks.find((b) => b.id === drawerId) || null;
  const selectedIndex = blocks.findIndex((b) => b.id === selectedId);

  const updateContent = useCallback(
    (blockId: string, content: Record<string, any>) => {
      onChange(blocks.map((b) => (b.id === blockId ? { ...b, content } : b)));
    },
    [blocks, onChange]
  );

  const moveBlock = useCallback(
    (index: number, dir: -1 | 1) => {
      const target = index + dir;
      if (target < 0 || target >= blocks.length) return;
      const updated = [...blocks];
      [updated[index], updated[target]] = [updated[target], updated[index]];
      onChange(updated.map((b, i) => ({ ...b, sort_order: i })));
    },
    [blocks, onChange]
  );

  const removeBlock = useCallback(
    (id: string) => {
      onChange(blocks.filter((b) => b.id !== id).map((b, i) => ({ ...b, sort_order: i })));
      if (selectedId === id) setSelectedId(null);
      if (drawerId === id) setDrawerId(null);
    },
    [blocks, onChange, selectedId, drawerId]
  );

  const addBlock = useCallback(
    (type: string) => {
      const insertIdx = selectedIndex >= 0 ? selectedIndex + 1 : blocks.length;
      const newBlock: Block = {
        id: crypto.randomUUID(),
        block_type: type,
        content: {},
        sort_order: insertIdx,
      };
      const updated = [...blocks];
      updated.splice(insertIdx, 0, newBlock);
      onChange(updated.map((b, i) => ({ ...b, sort_order: i })));
      setSelectedId(newBlock.id);
      if (NEEDS_DRAWER.has(type)) {
        setDrawerId(newBlock.id);
      }
    },
    [blocks, onChange, selectedIndex]
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (drawerId) setDrawerId(null);
        else setSelectedId(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [drawerId]);

  return (
    <div className="relative" onClick={() => setSelectedId(null)}>
      {/* Hero preview */}
      {article.hero_image_url ? (
        <div className="relative w-full min-h-[50vh] overflow-hidden">
          <img src={article.hero_image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
          <div className="relative z-10 flex flex-col justify-end h-full min-h-[50vh] px-6 md:px-16 pb-12 md:pb-20">
            <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl text-white uppercase tracking-wide mb-2 max-w-[1200px]">
              {article.project_name || "Untitled Project"}
            </h1>
            {article.system_name && (
              <p className="font-sans text-sm text-white/70">{article.system_name}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="px-6 md:px-16 py-16 border-b border-border">
          <h1 className="font-serif text-3xl md:text-5xl text-foreground">
            {article.project_name || "Untitled Project"}
          </h1>
        </div>
      )}

      {/* Blocks */}
      {blocks.map((block, index) => {
        const isSelected = selectedId === block.id;
        const isInlineEditable = INLINE_TEXT_BLOCKS.has(block.block_type);

        return (
          <div
            key={block.id}
            className={`relative group/block transition-all ${
              isSelected
                ? "ring-2 ring-primary/40 ring-offset-4 ring-offset-muted z-10"
                : "hover:ring-1 hover:ring-border hover:ring-offset-2 hover:ring-offset-muted"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(block.id);
              // Auto-open drawer for non-inline-editable blocks
              if (!isInlineEditable) {
                setDrawerId(block.id);
              }
            }}
          >
            {/* Floating toolbar */}
            {isSelected && (
              <FloatingToolbar
                label={BLOCK_LABELS[block.block_type] || block.block_type}
                index={index}
                total={blocks.length}
                hasDrawer={NEEDS_DRAWER.has(block.block_type)}
                onMove={(dir) => moveBlock(index, dir)}
                onOpenDrawer={() => setDrawerId(block.id)}
                onRemove={() => removeBlock(block.id)}
              />
            )}

            {/* Hover label */}
            {!isSelected && (
              <div className="absolute -top-3 left-4 z-20 opacity-0 group-hover/block:opacity-100 transition-opacity rounded bg-muted-foreground/80 px-2 py-0.5 font-sans text-[10px] text-background uppercase tracking-wider">
                {BLOCK_LABELS[block.block_type] || block.block_type}
              </div>
            )}

            {/* Block content — inline editable or rendered */}
            {isSelected && isInlineEditable ? (
              <InlineTextBlock
                block={block}
                onUpdate={(content) => updateContent(block.id, content)}
              />
            ) : (
              <BlockRenderer block={block} />
            )}
          </div>
        );
      })}

      {/* Add block */}
      <div className="flex justify-center py-10" onClick={(e) => e.stopPropagation()}>
        <BlockTypeSelector onAdd={addBlock} />
      </div>

      {/* Settings drawer */}
      {drawerBlock && (
        <SettingsDrawer
          block={drawerBlock}
          onClose={() => setDrawerId(null)}
          onChange={(c) => updateContent(drawerBlock.id, c)}
        />
      )}
    </div>
  );
}

/* ─── Inline Text Block ─── */
/* Renders the block layout with contentEditable fields directly in React */

function InlineTextBlock({
  block,
  onUpdate,
}: {
  block: Block;
  onUpdate: (content: Record<string, any>) => void;
}) {
  const contentRef = useRef(block.content);
  contentRef.current = block.content;

  const handleFieldChange = useCallback(
    (key: string, value: string) => {
      onUpdate({ ...contentRef.current, [key]: value });
    },
    [onUpdate]
  );

  switch (block.block_type) {
    case "text_section":
      return (
        <section className="mx-auto max-w-[1200px] px-6 py-16">
          {block.content.showDivider && <div className="mb-12 h-px w-16 bg-border" />}
          <EditableField
            value={block.content.title || ""}
            placeholder="Section title…"
            onChange={(v) => handleFieldChange("title", v)}
            className="font-serif text-2xl md:text-3xl text-foreground mb-6 outline-none"
            tag="h2"
          />
          <EditableField
            value={block.content.body || ""}
            placeholder="Write your content here…"
            onChange={(v) => handleFieldChange("body", v)}
            className="max-w-[800px] font-sans text-base leading-relaxed text-muted-foreground whitespace-pre-wrap outline-none"
            tag="div"
            multiline
          />
        </section>
      );

    case "two_column":
      return (
        <section className="mx-auto max-w-[1200px] px-6 py-16">
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center`}
            style={block.content.reversed ? { direction: "rtl" } : undefined}
          >
            <div style={block.content.reversed ? { direction: "ltr" } : undefined}>
              {block.content.imageUrl ? (
                <img src={block.content.imageUrl} alt="" className="w-full object-cover" />
              ) : (
                <div className="w-full aspect-[4/3] bg-muted border-2 border-dashed border-border flex items-center justify-center text-muted-foreground text-sm">
                  No image
                </div>
              )}
            </div>
            <div style={block.content.reversed ? { direction: "ltr" } : undefined}>
              <EditableField
                value={block.content.title || ""}
                placeholder="Column title…"
                onChange={(v) => handleFieldChange("title", v)}
                className="font-serif text-2xl text-foreground mb-4 outline-none"
                tag="h3"
              />
              <EditableField
                value={block.content.text || ""}
                placeholder="Write column text…"
                onChange={(v) => handleFieldChange("text", v)}
                className="font-sans text-base leading-relaxed text-muted-foreground whitespace-pre-wrap outline-none"
                tag="p"
                multiline
              />
            </div>
          </div>
        </section>
      );

    case "cta":
      return (
        <section className="mx-auto max-w-[1200px] px-6 py-20 text-center">
          <EditableField
            value={block.content.headline || ""}
            placeholder="CTA headline…"
            onChange={(v) => handleFieldChange("headline", v)}
            className="font-serif text-3xl md:text-4xl text-foreground mb-4 outline-none"
            tag="h2"
          />
          <EditableField
            value={block.content.subtext || ""}
            placeholder="Supporting text…"
            onChange={(v) => handleFieldChange("subtext", v)}
            className="font-sans text-base text-muted-foreground mb-8 outline-none"
            tag="p"
          />
          {block.content.buttonText && (
            <span className="inline-block bg-primary text-primary-foreground px-8 py-3 font-sans text-sm uppercase tracking-wider">
              {block.content.buttonText}
            </span>
          )}
        </section>
      );

    default:
      return <BlockRenderer block={block} />;
  }
}

/* ─── Editable Field ─── */

function EditableField({
  value,
  placeholder,
  onChange,
  className,
  tag: Tag = "div",
  multiline = false,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  className: string;
  tag?: "h2" | "h3" | "p" | "div";
  multiline?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const lastValueRef = useRef(value);

  // Only sync from props when value genuinely changed externally
  useEffect(() => {
    if (ref.current && value !== lastValueRef.current) {
      ref.current.textContent = value;
      lastValueRef.current = value;
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!ref.current) return;
    const newVal = ref.current.textContent || "";
    lastValueRef.current = newVal;
    onChange(newVal);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!multiline && e.key === "Enter") {
        e.preventDefault();
      }
    },
    [multiline]
  );

  return (
    <Tag
      ref={ref as any}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      className={`${className} empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/40 focus:ring-1 focus:ring-primary/20 focus:rounded-sm cursor-text`}
      data-placeholder={placeholder}
      // Set initial content
      dangerouslySetInnerHTML={{ __html: value || "" }}
    />
  );
}

/* ─── Floating Toolbar ─── */

function FloatingToolbar({
  label,
  index,
  total,
  hasDrawer,
  onMove,
  onOpenDrawer,
  onRemove,
}: {
  label: string;
  index: number;
  total: number;
  hasDrawer: boolean;
  onMove: (dir: -1 | 1) => void;
  onOpenDrawer: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className="absolute -top-10 left-1/2 -translate-x-1/2 z-30 flex items-center gap-0.5 bg-foreground text-background rounded-lg shadow-xl px-1 py-0.5 animate-in fade-in-0 zoom-in-95 duration-150"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="px-2 py-1 text-[10px] font-sans uppercase tracking-wider opacity-70">
        {label}
      </span>
      <div className="w-px h-4 bg-background/20" />
      <Button variant="ghost" size="icon" className="h-7 w-7 text-background hover:text-background hover:bg-background/20" onClick={() => onMove(-1)} disabled={index === 0}>
        <ChevronUp size={14} />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-background hover:text-background hover:bg-background/20" onClick={() => onMove(1)} disabled={index === total - 1}>
        <ChevronDown size={14} />
      </Button>
      {hasDrawer && (
        <Button variant="ghost" size="icon" className="h-7 w-7 text-background hover:text-background hover:bg-background/20" onClick={onOpenDrawer}>
          <Settings2 size={14} />
        </Button>
      )}
      <div className="w-px h-4 bg-background/20" />
      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-background/20" onClick={onRemove}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

/* ─── Settings Drawer ─── */

function SettingsDrawer({
  block,
  onClose,
  onChange,
}: {
  block: Block;
  onClose: () => void;
  onChange: (content: Record<string, any>) => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 h-full w-[400px] bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Settings2 size={16} className="text-muted-foreground" />
          <h3 className="font-sans text-sm font-medium text-foreground flex-1">
            {BLOCK_LABELS[block.block_type] || block.block_type} Settings
          </h3>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X size={14} />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <BlockEditForm
            blockType={block.block_type}
            content={block.content}
            onChange={onChange}
          />
        </div>
      </div>
    </>
  );
}
